import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { CheckedCIcon, ContactInfoCIcon, UncheckedCIcon } from '~/assets/images';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcFormModal from '~/components/General/OlcFormModal';
import OlcToggle from '~/components/General/OlcToggle';

import { DISALLOWED_DOMAINS, ENGAGEMENT_TYPES } from '~/utils/constants';
import { validURL } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

const PURL_TYPES = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
};

const URL_CUSTOM_FIELD_TYPE = 'url';

const PURL_TYPE_OPTIONS = [
  {
    id: PURL_TYPES.STATIC,
    title: 'Static PURL',
    menuTitle: 'Static PURL',
    description: 'Use when all visitors should go to the same PURL.',
  },
  {
    id: PURL_TYPES.DYNAMIC,
    title: 'Dynamic PURL',
    menuTitle: 'Dynamic PURL',
    description: 'Use when each contact has a personalized URL.',
  },
];

const DEFAULT_PURL_FORM_VALUES = {
  purlName: '',
  purlType: PURL_TYPES.STATIC,
  customFieldId: null,
  dynamicUrl: '',
  staticUrl: '',
  fallbackUrl: '',
  addBranding: false,
  brandName: '',
  sendEmail: false,
  notificationEmail: '',
};

const DEFAULT_PURL_EXAMPLE_DOMAIN = 'dmoffer.com';

const getPurlRecordValue = (record, fieldName) =>
  record?.[fieldName] || record?.qrCodeJson?.[fieldName] || '';

const formatCustomFieldUrlLabel = (customField = {}) => {
  const displayName =
    customField.value || customField.title || customField.label || customField.name;

  if (displayName) {
    return String(displayName).trim();
  }

  const fallbackLabel = customField.key
    ? customField.key.replace(/[{}]/g, '').replace(/\./g, '_')
    : '';
  const normalizedLabel = String(fallbackLabel).trim();

  if (!normalizedLabel) return '';

  return normalizedLabel;
};

const normalizeCustomFieldUrlLabel = (value = '') => {
  const normalizedLabel = String(value).trim().toLowerCase().replace(/\s+/g, '_');

  if (!normalizedLabel) return '';

  return normalizedLabel.startsWith('cf_') ? normalizedLabel : `cf_${normalizedLabel}`;
};

const getSelectedPurlType = (record) => {
  const dynamicUrl = getPurlRecordValue(record, 'dynamicUrl');
  if (
    dynamicUrl ||
    record?.urlField === PURL_TYPES.DYNAMIC ||
    record?.url_field === PURL_TYPES.DYNAMIC
  ) {
    return PURL_TYPES.DYNAMIC;
  }

  return PURL_TYPES.STATIC;
};

const getDefaultPurlFormValues = (isNotificationEnabled, userEmail) => ({
  ...DEFAULT_PURL_FORM_VALUES,
  notificationEmail: isNotificationEnabled ? userEmail : '',
});

const normalizeBrandName = (brandName = '') =>
  String(brandName).toLowerCase().replace(/[^a-z0-9]/g, '');

const getPurlFormValues = (record, isNotificationEnabled = false, userEmail = '') => {
  if (!record) return getDefaultPurlFormValues(isNotificationEnabled, userEmail);

  const brandName = normalizeBrandName(getPurlRecordValue(record, 'brandName'));
  const purlName =
    getPurlRecordValue(record, 'purlName') ||
    record?.name ||
    record?.qrCodeJson?.name ||
    record?.qrCodeJson?.utm_campaign_name ||
    '';

  return {
    purlName,
    purlType: getSelectedPurlType(record),
    customFieldId: record?.customFieldId || record?.custom_field_id || null,
    dynamicUrl:
      getPurlRecordValue(record, 'dynamicUrl') || formatCustomFieldUrlLabel(record?.customField),
    staticUrl: getPurlRecordValue(record, 'staticUrl'),
    fallbackUrl: getPurlRecordValue(record, 'fallbackUrl') || record?.qrCodeJson?.url || '',
    addBranding: Boolean(brandName),
    brandName,
    sendEmail: isNotificationEnabled && Boolean(record?.sendEmail ?? record?.qrCodeJson?.sendEmail),
    notificationEmail: isNotificationEnabled
      ? (record?.notificationEmail ?? record?.qrCodeJson?.notificationEmail ?? userEmail)
      : '',
  };
};

const getPurlExampleDomain = () =>
  String(import.meta.env.VITE_APP_PURL_BASE_URL || DEFAULT_PURL_EXAMPLE_DOMAIN)
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');

const getPurlExample = (brandName, addBranding) => {
  const normalizedBrandName = addBranding ? normalizeBrandName(brandName) : '';

  return `${normalizedBrandName ? `${normalizedBrandName}.` : ''}${getPurlExampleDomain()}/tracking-id`;
};

const containsDisallowedDomains = (value) =>
  DISALLOWED_DOMAINS.some((substring) => String(value || '').includes(substring));

const validateUrlValue = (value, label) => {
  if (!validURL(value)) {
    return `Enter a valid ${label}.`;
  }

  if (containsDisallowedDomains(value)) {
    return `${label} contains a disallowed domain.`;
  }

  return '';
};

const PurlForm = ({
  openModal,
  handleCloseModal,
  selectedPurl,
  onCreatePurl,
  onUpdatePurl,
  isEditMode = false,
  customFields = [],
  onCreateCustomFieldClick,
  pendingCustomFieldSelection,
  onPendingCustomFieldSelectionConsumed,
}) => {
  const formId = isEditMode ? 'purl-edit-form' : 'purl-create-form';
  const previousOpenRef = useRef(false);
  const userEmail = useSelector((state) => state.userReducers?.user?.email || '');
  const isQrScanNotificationEnabled = useSelector(
    (state) => state.userReducers?.user?.subscribedPlan?.isDefault === false
  );

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(DEFAULT_PURL_FORM_VALUES);
  const [errors, setErrors] = useState({});

  const selectedPurlType = useMemo(
    () => PURL_TYPE_OPTIONS.find(({ id }) => id === formValues.purlType) || PURL_TYPE_OPTIONS[0],
    [formValues.purlType]
  );
  const isDynamicPurl = formValues.purlType === PURL_TYPES.DYNAMIC;
  const customFieldOptions = useMemo(
    () =>
      (customFields || [])
        .filter((customField) => customField.type === URL_CUSTOM_FIELD_TYPE)
        .map((customField) => ({
          id: customField.id,
          title: formatCustomFieldUrlLabel(customField),
        }))
        .filter(({ id, title }) => id && title),
    [customFields]
  );
  const selectedUrlField = useMemo(
    () =>
      customFieldOptions.find(({ id }) => String(id) === String(formValues.customFieldId)) ||
      (formValues.customFieldId && formValues.dynamicUrl
        ? {
            id: formValues.customFieldId,
            title: formValues.dynamicUrl,
          }
        : null),
    [customFieldOptions, formValues.customFieldId, formValues.dynamicUrl]
  );
  const isPurlScanNotificationChecked = isQrScanNotificationEnabled && formValues.sendEmail;
  const purlExample = useMemo(
    () => getPurlExample(formValues.brandName, formValues.addBranding),
    [formValues.addBranding, formValues.brandName]
  );

  const onClose = () => {
    handleCloseModal();
    setLoading(false);
    setFormValues(DEFAULT_PURL_FORM_VALUES);
    setErrors({});
  };

  const clearError = (fieldName) => {
    if (!errors[fieldName]) return;
    setErrors((prev) => {
      const { [fieldName]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleChange = (fieldName) => (event) => {
    const nextValue =
      fieldName === 'brandName' ? normalizeBrandName(event.target.value) : event.target.value;
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: nextValue,
    }));
    clearError(fieldName);
  };

  const handleClearField = (fieldName) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: '',
    }));
    clearError(fieldName);
  };

  const getClearButtonAdornment = (fieldName, label) => {
    const fieldValue = formValues[fieldName];

    return (
      fieldValue?.trim() && (
        <InputAdornment
          className="qrClearInputAdornment"
          position="end"
          sx={{ width: '16px', marginRight: '8px' }}
        >
          <IconButton
            aria-label={`Clear ${label}`}
            className="cpClearInputBtn smallClearIcon qrClearInputBtn"
            size="small"
            type="button"
            tabIndex={-1}
            onClick={() => handleClearField(fieldName)}
            edge="end"
            sx={{ padding: '4px' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const handlePurlTypeSelect = (value) => {
    const nextPurlType = value?.id || PURL_TYPES.STATIC;
    setFormValues((prev) => ({
      ...prev,
      purlType: nextPurlType,
      customFieldId: nextPurlType === PURL_TYPES.DYNAMIC ? prev.customFieldId : null,
      fallbackUrl: nextPurlType === PURL_TYPES.DYNAMIC ? prev.fallbackUrl : '',
    }));
    setErrors((prev) => {
      const {
        dynamicUrl: _dynamicUrl,
        staticUrl: _staticUrl,
        fallbackUrl: _fallbackUrl,
        ...rest
      } = prev;
      return rest;
    });
  };

  const handleUrlFieldSelect = (value) => {
    setFormValues((prev) => ({
      ...prev,
      customFieldId: value?.id || null,
      dynamicUrl: value?.title || '',
    }));
    clearError('customFieldId');
    clearError('dynamicUrl');
  };

  const handleCheckboxChange = (event) => {
    if (!isQrScanNotificationEnabled) {
      setFormValues((prev) => ({
        ...prev,
        sendEmail: false,
        notificationEmail: '',
      }));
      return;
    }

    const isChecked = event.target.checked;
    setFormValues((prev) => ({
      ...prev,
      sendEmail: isChecked,
      notificationEmail: isChecked ? prev.notificationEmail || userEmail : '',
    }));
    setErrors((prev) => {
      const { notificationEmail: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleBrandingToggle = (checked) => {
    setFormValues((prev) => ({
      ...prev,
      addBranding: checked,
      brandName: checked ? prev.brandName : '',
    }));
    if (!checked) {
      clearError('brandName');
    }
  };

  const validateForm = () => {
    const trimmedPurlName = formValues.purlName.trim();
    const trimmedDynamicUrl = (formValues.dynamicUrl || selectedUrlField?.title || '').trim();
    const trimmedStaticUrl = formValues.staticUrl.trim();
    const trimmedFallbackUrl = formValues.fallbackUrl.trim();
    const trimmedBrandName = normalizeBrandName(formValues.brandName);
    const trimmedNotificationEmail = formValues.notificationEmail.trim();
    const validationErrors = {};

    if (!trimmedPurlName) {
      validationErrors.purlName = 'PURL name is required.';
    }

    if (isDynamicPurl) {
      if (!formValues.customFieldId) {
        validationErrors.customFieldId = 'Select a URL field.';
      }

      if (!trimmedDynamicUrl) {
        validationErrors.dynamicUrl = 'Dynamic PURL is required.';
      }
    } else if (!trimmedStaticUrl) {
      validationErrors.staticUrl = 'Static PURL is required.';
    } else {
      const staticUrlError = validateUrlValue(trimmedStaticUrl, 'Static PURL');
      if (staticUrlError) {
        validationErrors.staticUrl = staticUrlError;
      }
    }

    if (isDynamicPurl && !trimmedFallbackUrl) {
      validationErrors.fallbackUrl = 'Fallback URL is required.';
    } else if (isDynamicPurl) {
      const fallbackUrlError = validateUrlValue(trimmedFallbackUrl, 'Fallback URL');
      if (fallbackUrlError) {
        validationErrors.fallbackUrl = fallbackUrlError;
      }
    }

    if (isQrScanNotificationEnabled && formValues.sendEmail) {
      if (!trimmedNotificationEmail) {
        validationErrors.notificationEmail = 'Notification email is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedNotificationEmail)) {
        validationErrors.notificationEmail = 'Enter a valid email.';
      }
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return null;
    }

    const nextFallbackUrl = isDynamicPurl ? trimmedFallbackUrl : '';

    if (
      formValues.purlName !== trimmedPurlName ||
      formValues.dynamicUrl !== trimmedDynamicUrl ||
      formValues.staticUrl !== trimmedStaticUrl ||
      formValues.fallbackUrl !== nextFallbackUrl ||
      formValues.brandName !== trimmedBrandName ||
      formValues.notificationEmail !== trimmedNotificationEmail
    ) {
      setFormValues((prev) => ({
        ...prev,
        purlName: trimmedPurlName,
        dynamicUrl: trimmedDynamicUrl,
        staticUrl: trimmedStaticUrl,
        fallbackUrl: nextFallbackUrl,
        brandName: trimmedBrandName,
        notificationEmail: trimmedNotificationEmail,
      }));
    }

    return {
      purlName: trimmedPurlName,
      dynamicUrl: trimmedDynamicUrl,
      staticUrl: trimmedStaticUrl,
      fallbackUrl: nextFallbackUrl,
      brandName: trimmedBrandName,
      notificationEmail: trimmedNotificationEmail,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validatedValues = validateForm();
    if (!validatedValues) return;

    const shouldSendNotificationEmail = isQrScanNotificationEnabled && formValues.sendEmail;
    const purlUrl = isDynamicPurl ? validatedValues.fallbackUrl : validatedValues.staticUrl;
    const payload = {
      engagementType: ENGAGEMENT_TYPES.PURL,
      name: validatedValues.purlName,
      url: purlUrl,
      purlName: validatedValues.purlName,
      dynamicUrl: isDynamicPurl ? validatedValues.dynamicUrl : '',
      staticUrl: isDynamicPurl ? '' : validatedValues.staticUrl,
      fallbackUrl: isDynamicPurl ? validatedValues.fallbackUrl : '',
      brandName: formValues.addBranding ? validatedValues.brandName : '',
      sendEmail: shouldSendNotificationEmail,
      notificationEmail: shouldSendNotificationEmail ? validatedValues.notificationEmail : null,
      url_field: formValues.purlType,
      custom_field_id: isDynamicPurl ? formValues.customFieldId : null,
      qrCodeJson: {
        name: validatedValues.purlName,
        purlName: validatedValues.purlName,
        dynamicUrl: isDynamicPurl ? validatedValues.dynamicUrl : '',
        staticUrl: isDynamicPurl ? '' : validatedValues.staticUrl,
        fallbackUrl: isDynamicPurl ? validatedValues.fallbackUrl : '',
        url: purlUrl,
        brandName: formValues.addBranding ? validatedValues.brandName : '',
      },
    };

    try {
      setLoading(true);
      const response =
        isEditMode && selectedPurl?.id
          ? await onUpdatePurl(selectedPurl.id, payload)
          : await onCreatePurl(payload);

      if (response?.status === 200 || response?.status === 201) {
        onClose();
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (!openModal) {
      previousOpenRef.current = false;
      setLoading(false);
      setFormValues(getDefaultPurlFormValues(isQrScanNotificationEnabled, userEmail));
      setErrors({});
      return;
    }

    if (!previousOpenRef.current) {
      setFormValues(
        isEditMode
          ? getPurlFormValues(selectedPurl, isQrScanNotificationEnabled, userEmail)
          : getDefaultPurlFormValues(isQrScanNotificationEnabled, userEmail)
      );
      setErrors({});
      setLoading(false);
    }

    previousOpenRef.current = true;
  }, [isEditMode, isQrScanNotificationEnabled, openModal, selectedPurl, userEmail]);

  useEffect(() => {
    if (!pendingCustomFieldSelection || !customFieldOptions.length) {
      return;
    }

    const matchingOption = customFieldOptions.find((option) => {
      const pendingId = pendingCustomFieldSelection.id ?? pendingCustomFieldSelection.customFieldId;
      const pendingTitle =
        pendingCustomFieldSelection.title ||
        pendingCustomFieldSelection.label ||
        formatCustomFieldUrlLabel(pendingCustomFieldSelection);

      return (
        (pendingId && String(option.id) === String(pendingId)) ||
        (pendingTitle &&
          normalizeCustomFieldUrlLabel(option.title) === normalizeCustomFieldUrlLabel(pendingTitle))
      );
    });

    if (!matchingOption) {
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      purlType: PURL_TYPES.DYNAMIC,
      customFieldId: matchingOption.id,
      dynamicUrl: matchingOption.title,
    }));
    setErrors((prev) => {
      const { customFieldId: _customFieldId, dynamicUrl: _dynamicUrl, ...rest } = prev;
      return rest;
    });
    onPendingCustomFieldSelectionConsumed?.();
  }, [customFieldOptions, onPendingCustomFieldSelectionConsumed, pendingCustomFieldSelection]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={
        isEditMode
          ? MESSAGES.SETTINGS.PURLS.EDIT.TITLE
          : MESSAGES.SETTINGS.PURLS.ADD_NEW_MODAL.TITLE
      }
      className="qrCodeCreateModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </OlcButton>
          <OlcButton variant="primary" type="submit" form={formId} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              'Save'
            )}
          </OlcButton>
        </>
      }
    >
      <Box component="form" id={formId} className="qrCodeForm" onSubmit={handleSubmit} noValidate>
        <Box className="modalFormWrapper qrWrpper purlFormWrapper">
          <Box className="modalFlexAreaQR">
            <Box>
              <InputLabel htmlFor="purlName" className="formLabel">
                PURL Name<span className="requiredField">*</span>
              </InputLabel>
              <TextField
                placeholder="Enter PURL name"
                fullWidth
                name="purlName"
                className="qrlgFields inputField formField"
                onChange={handleChange('purlName')}
                value={formValues.purlName.trimStart()}
                autoComplete="off"
                InputProps={{ endAdornment: getClearButtonAdornment('purlName', 'PURL Name') }}
              />
              {Boolean(errors.purlName) && (
                <Typography className="errorMessage">{errors.purlName}</Typography>
              )}
            </Box>

            <Box>
              <InputLabel
                htmlFor={isDynamicPurl ? 'dynamicUrl' : 'staticUrl'}
                className="formLabel"
              >
                PURL
                <span className="requiredField">*</span>
              </InputLabel>
              <Box className="qrUrlFieldWrapper">
                <Box className="olcDropdownSearch olcDropdownSearch--default multiSelectRedesign qrUrlTypeDropdown">
                  <Select
                    displayEmpty
                    inputProps={{ 'aria-label': 'PURL Type' }}
                    value={selectedPurlType?.id || PURL_TYPES.STATIC}
                    onChange={(event) => {
                      handlePurlTypeSelect({ id: event.target.value });
                    }}
                    input={<OutlinedInput />}
                    renderValue={() => selectedPurlType?.title || 'Static PURL'}
                    className="basicSelect"
                    MenuProps={{
                      disableScrollLock: true,
                      disablePortal: true,
                      marginThreshold: 0,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      PaperProps: {
                        className: 'olcDropdownSearchOptions qrUrlTypeDropdownMenu',
                      },
                      MenuListProps: {
                        className: 'qrUrlTypeDropdownList',
                      },
                    }}
                    IconComponent={(props) => (
                      <OlcDropdownChevron
                        {...props}
                        style={{
                          ...props.style,
                          marginTop: '-2.5px',
                          right: '20px',
                          top: '50%',
                        }}
                      />
                    )}
                  >
                    {PURL_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.id} value={option.id} disableRipple>
                        <Box>
                          <Typography className="qrUrlTypeOptionTitle">
                            {option.menuTitle}
                          </Typography>
                          <Typography className="qrUrlTypeOptionDescription">
                            {option.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {isDynamicPurl ? (
                  <OlcDropdownSearch
                    placeHolderText="Select URL Field"
                    searchFieldPlaceholderText="URL Field"
                    options={customFieldOptions}
                    selectedValue={selectedUrlField || null}
                    setSelectedValue={handleUrlFieldSelect}
                    className="qrUrlCustomFieldDropdown"
                    component="multipleIds"
                    noOptionText="No custom fields"
                    showSelectedValueTooltip
                    footerAction={
                      onCreateCustomFieldClick
                        ? {
                            label: 'Create New Custom Field',
                            onClick: onCreateCustomFieldClick,
                          }
                        : null
                    }
                  />
                ) : (
                  <TextField
                    placeholder="Enter Static PURL"
                    fullWidth
                    name="staticUrl"
                    className="qrlgFields inputField formField"
                    onChange={handleChange('staticUrl')}
                    value={formValues.staticUrl.trimStart()}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: getClearButtonAdornment('staticUrl', 'Static PURL'),
                    }}
                  />
                )}
              </Box>
              {Boolean(errors.dynamicUrl) && (
                <Typography className="errorMessage">{errors.dynamicUrl}</Typography>
              )}
              {Boolean(errors.customFieldId) && (
                <Typography className="errorMessage">{errors.customFieldId}</Typography>
              )}
              {Boolean(errors.staticUrl) && (
                <Typography className="errorMessage">{errors.staticUrl}</Typography>
              )}
            </Box>

            {isDynamicPurl && (
              <Box>
                <InputLabel htmlFor="fallbackUrl" className="formLabel">
                  Fallback URL<span className="requiredField">*</span>
                </InputLabel>
                <TextField
                  placeholder="Enter Fallback URL"
                  fullWidth
                  name="fallbackUrl"
                  className="qrlgFields inputField formField"
                  onChange={handleChange('fallbackUrl')}
                  value={formValues.fallbackUrl.trimStart()}
                  autoComplete="off"
                  InputProps={{
                    endAdornment: getClearButtonAdornment('fallbackUrl', 'Fallback URL'),
                  }}
                />
                {Boolean(errors.fallbackUrl) && (
                  <Typography className="errorMessage">{errors.fallbackUrl}</Typography>
                )}
              </Box>
            )}

            <Box className="qrNotificationCheckbox">
              <FormControlLabel
                className={`qrNotificationCheck${
                  isQrScanNotificationEnabled ? '' : ' qrNotificationCheckDisabled'
                }`}
                control={
                  <Checkbox
                    className="customCheckbox checked"
                    checked={isPurlScanNotificationChecked}
                    disabled={!isQrScanNotificationEnabled}
                    onChange={handleCheckboxChange}
                    icon={<img src={UncheckedCIcon} alt="unchecked" />}
                    checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                    style={{ pointerEvents: 'auto' }}
                    disableRipple={true}
                  />
                }
                label={
                  <Box component="span" className="qrNotificationLabel">
                    <span className="qrNotificationLabelText">Send notification email when PURL is used</span>
                    {!isQrScanNotificationEnabled && (
                      <Tooltip
                        title={MESSAGES.SETTINGS.QR_CODES.SCAN_NOTIFICATION_UPGRADE}
                        placement="bottom-start"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                              maxWidth: '260px',
                              padding: '8px !important',
                            },
                          },
                        }}
                      >
                        <img src={ContactInfoCIcon} alt="info" className="qrNotificationInfoIcon" />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </Box>

            {isPurlScanNotificationChecked && (
              <Box>
                <InputLabel htmlFor="notificationEmail" className="formLabel">
                  Notification Email<span className="requiredField">*</span>
                </InputLabel>
                <TextField
                  placeholder="Enter notification email"
                  fullWidth
                  name="notificationEmail"
                  className="qrlgFields inputField formField"
                  onChange={handleChange('notificationEmail')}
                  value={formValues.notificationEmail.trimStart()}
                  autoComplete="off"
                />
                {Boolean(errors.notificationEmail) && (
                  <Typography className="errorMessage">{errors.notificationEmail}</Typography>
                )}
              </Box>
            )}

            <Box className="purlBrandingSection">
              <Typography className="purlBrandingTitle">Add Branding</Typography>
              <Typography className="purlBrandingHelp">
                Would you like to brand your PURL? Optional.
              </Typography>
              <Box
                className="purlBrandingToggleRow"
                onClick={() => handleBrandingToggle(!formValues.addBranding)}
              >
                <Box onClick={(event) => event.stopPropagation()}>
                  <OlcToggle
                    checked={formValues.addBranding}
                    onChange={handleBrandingToggle}
                    ariaLabel="Add PURL branding toggle"
                    className="purlBrandingToggle"
                  />
                </Box>
                <Typography className="purlBrandingToggleLabel">
                  Add a static name (ex. Company Name) to your PURL
                </Typography>
              </Box>
            </Box>

            {formValues.addBranding && (
              <>
                <Box>
                  <InputLabel htmlFor="brandName" className="formLabel">
                    Brand Name
                  </InputLabel>
                  <TextField
                    placeholder="e.g., openlettermarketing"
                    fullWidth
                    name="brandName"
                    className="qrlgFields inputField formField"
                    onChange={handleChange('brandName')}
                    value={formValues.brandName}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: getClearButtonAdornment('brandName', 'Brand Name'),
                    }}
                  />
                </Box>

                <Box className="purlExampleCard">
                  <Typography className="purlExampleLabel">PURL EXAMPLE</Typography>
                  <Typography className="purlExampleValue">{purlExample}</Typography>
                </Box>
              </>
            )}

            {isEditMode && (
              <Box>
                <Typography className="helperText">
                  Any changes you make to this PURL will apply immediately. If the
                  <br /> PURL has already been mailed, anyone who visits it after your update
                  <br /> will see the new settings.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </OlcFormModal>
  );
};

export default PurlForm;
